<template>
  <div
    class="base-select"
    :class="[
      sizeClass,
      {
        'base-select--open': isOpen,
        'base-select--disabled': disabled,
        'base-select--error': hasError,
      },
    ]"
    ref="root"
  >
    <button
      type="button"
      class="base-select__trigger"
      :disabled="disabled"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click.stop="toggleOpen"
      @keydown="onTriggerKeydown"
    >
      <span class="base-select__label" :class="{ 'base-select__placeholder': !selectedOption }">
        {{ displayLabel }}
      </span>
      <i class="fas fa-chevron-down base-select__chevron"></i>
    </button>

    <div
      v-if="isOpen"
      ref="menu"
      class="base-select__menu"
      :class="{ 'base-select__menu--drop-up': menuDropUp }"
      :style="menuMaxHeight ? { maxHeight: menuMaxHeight } : undefined"
      role="listbox"
      :aria-labelledby="ariaLabelledby || undefined"
      @click.stop
      @mousedown.stop
    >
      <div v-if="searchable" class="base-select__search">
        <input
          ref="searchInput"
          type="text"
          class="form-control form-control-sm"
          :placeholder="searchPlaceholder"
          v-model="searchText"
          @keydown.stop
        />
      </div>
      <ul class="base-select__list" role="presentation">
        <li
          v-if="clearable && selectedOption"
          class="base-select__option base-select__option--clear"
          role="option"
          @click="onClear"
        >
          <i class="fas fa-times me-2 text-secondary"></i>{{ clearLabel }}
        </li>
        <li
          v-if="allowCreate && searchText.trim() && !hasExactMatch"
          class="base-select__option base-select__option--create"
          role="option"
          @click="onCreateNew"
        >
          <i class="fas fa-plus-circle me-2"></i>{{ createLabel }} "{{ searchText.trim() }}"
        </li>
        <li
          v-for="(opt, idx) in filteredOptions"
          :key="`opt-${idx}-${String(getValue(opt))}`"
          class="base-select__option"
          :class="{
            'base-select__option--selected': isSelected(opt),
            'base-select__option--disabled': isOptionDisabled(opt),
          }"
          role="option"
          :aria-selected="isSelected(opt) ? 'true' : 'false'"
          :aria-disabled="isOptionDisabled(opt) ? 'true' : 'false'"
          @click="onSelect(opt)"
        >
          <slot name="option" :option="opt" :selected="isSelected(opt)">
            {{ getLabel(opt) }}
          </slot>
        </li>
        <li
          v-if="filteredOptions.length === 0"
          class="base-select__option base-select__option--empty"
        >
          {{ emptyLabel }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/** Only one BaseSelect menu open at a time. */
let activeSelectInstance = null;

export default {
  name: "BaseSelect",
  props: {
    modelValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: String,
      default: "label",
    },
    optionValue: {
      type: String,
      default: "value",
    },
    optionDisabled: {
      type: String,
      default: "disabled",
    },
    placeholder: {
      type: String,
      default: "Select…",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearLabel: {
      type: String,
      default: "Clear selection",
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: "Search…",
    },
    emptyLabel: {
      type: String,
      default: "No options",
    },
    size: {
      type: String,
      default: "md",
      validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    ariaLabelledby: {
      type: String,
      default: "",
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    createLabel: {
      type: String,
      default: "Create",
    },
  },
  emits: ["update:modelValue", "change", "create"],
  data() {
    return {
      isOpen: false,
      searchText: "",
      menuDropUp: false,
      menuMaxHeight: null,
      menuPlacementLocked: false,
      outsideClickTimer: null,
    };
  },
  computed: {
    sizeClass() {
      return `base-select--${this.size}`;
    },
    selectedOption() {
      const v = this.modelValue;
      if (v === null || v === undefined || v === "") {
        return (
          this.options.find((opt) => {
            const ov = this.getValue(opt);
            return ov === null || ov === undefined || ov === "";
          }) || null
        );
      }
      return (
        this.options.find((opt) => {
          const ov = this.getValue(opt);
          // eslint-disable-next-line eqeqeq
          return ov == v;
        }) || null
      );
    },
    displayLabel() {
      if (this.selectedOption) return this.getLabel(this.selectedOption);
      if (this.allowCreate && this.modelValue) return String(this.modelValue);
      return this.placeholder;
    },
    filteredOptions() {
      if (!this.searchable || !this.searchText.trim()) return this.options;
      const q = this.searchText.trim().toLowerCase();
      return this.options.filter((opt) =>
        String(this.getLabel(opt)).toLowerCase().includes(q)
      );
    },
    hasExactMatch() {
      const q = this.searchText.trim().toLowerCase();
      if (!q) return false;
      return this.options.some((opt) => String(this.getLabel(opt)).toLowerCase() === q);
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.menuPlacementLocked = false;
        this.$nextTick(() => {
          this.updateMenuPlacement();
          requestAnimationFrame(() => {
            if (this.isOpen) this.updateMenuPlacement();
            if (this.searchable && this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          });
        });
        this.outsideClickTimer = setTimeout(() => {
          document.addEventListener("click", this.onOutsideClick, true);
        }, 0);
        window.addEventListener("resize", this.onPlacementResize);
        window.addEventListener("scroll", this.onPlacementScroll, true);
      } else {
        this.clearOutsideListeners();
        this.resetMenuPlacement();
      }
    },
    filteredOptions() {
      if (this.isOpen && !this.menuPlacementLocked) {
        this.$nextTick(() => this.updateMenuPlacement());
      }
    },
  },
  beforeUnmount() {
    if (activeSelectInstance === this) {
      activeSelectInstance = null;
    }
    this.clearOutsideListeners();
    this.closeMenu();
  },
  methods: {
    getLabel(opt) {
      if (opt === null || opt === undefined) return "";
      if (typeof opt === "object") return opt[this.optionLabel];
      return String(opt);
    },
    getValue(opt) {
      if (opt === null || opt === undefined) return null;
      if (typeof opt === "object") return opt[this.optionValue];
      return opt;
    },
    isSelected(opt) {
      const v = this.modelValue;
      const ov = this.getValue(opt);
      // eslint-disable-next-line eqeqeq
      return ov == v;
    },
    isOptionDisabled(opt) {
      if (opt === null || opt === undefined) return false;
      if (typeof opt !== "object") return false;
      return Boolean(opt[this.optionDisabled]);
    },
    toggleOpen() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.closeMenu();
        return;
      }
      if (activeSelectInstance && activeSelectInstance !== this) {
        activeSelectInstance.closeMenu();
      }
      this.isOpen = true;
      activeSelectInstance = this;
    },
    closeMenu() {
      this.isOpen = false;
      if (activeSelectInstance === this) {
        activeSelectInstance = null;
      }
    },
    clearOutsideListeners() {
      if (this.outsideClickTimer) {
        clearTimeout(this.outsideClickTimer);
        this.outsideClickTimer = null;
      }
      document.removeEventListener("click", this.onOutsideClick, true);
      window.removeEventListener("resize", this.onPlacementResize);
      window.removeEventListener("scroll", this.onPlacementScroll, true);
    },
    resetMenuPlacement() {
      this.menuDropUp = false;
      this.menuMaxHeight = null;
      this.menuPlacementLocked = false;
    },
    onPlacementScroll(e) {
      const menu = this.$refs.menu;
      if (menu?.contains(e.target)) return;
      this.updateMenuPlacement();
    },
    onPlacementResize() {
      this.menuPlacementLocked = false;
      this.updateMenuPlacement();
    },
    findScrollParent(el) {
      let parent = el?.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        if (
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          parent.scrollHeight > parent.clientHeight + 1
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    },
    getPlacementSpaces(triggerRect, root) {
      const viewportPadding = 8;
      let spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
      let spaceAbove = triggerRect.top - viewportPadding;

      const scrollParent = this.findScrollParent(root);
      if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        spaceBelow = Math.min(spaceBelow, parentRect.bottom - triggerRect.bottom - viewportPadding);
        spaceAbove = Math.min(spaceAbove, triggerRect.top - parentRect.top - viewportPadding);
      }

      return {
        below: Math.max(0, spaceBelow),
        above: Math.max(0, spaceAbove),
      };
    },
    estimateMenuHeight() {
      const menu = this.$refs.menu;
      if (menu) {
        return menu.scrollHeight;
      }
      const rowCount =
        this.filteredOptions.length +
        (this.clearable && this.selectedOption ? 1 : 0) +
        (this.searchable ? 1 : 0);
      return Math.min(Math.max(rowCount * 38 + 16, 80), 260);
    },
    updateMenuPlacement() {
      if (!this.isOpen) return;
      const root = this.$refs.root;
      if (!root) return;

      const triggerRect = root.getBoundingClientRect();
      if (triggerRect.width <= 0 || triggerRect.height <= 0) return;

      const gap = 4;
      const defaultMaxHeight = 260;
      const minMenuHeight = 120;

      const { below: spaceBelow, above: spaceAbove } = this.getPlacementSpaces(triggerRect, root);
      const naturalHeight = Math.min(this.estimateMenuHeight(), defaultMaxHeight);
      const neededBelow = naturalHeight + gap;

      if (!this.menuPlacementLocked) {
        const preferDropUp = spaceBelow < neededBelow && spaceAbove >= spaceBelow;
        this.menuDropUp = preferDropUp;
        if (this.$refs.menu) {
          this.menuPlacementLocked = true;
        }
      }

      const available = (this.menuDropUp ? spaceAbove : spaceBelow) - gap;
      this.menuMaxHeight = `${Math.max(minMenuHeight, Math.min(defaultMaxHeight, available))}px`;
    },
    onSelect(opt) {
      if (this.isOptionDisabled(opt)) return;
      const v = this.getValue(opt);
      this.$emit("update:modelValue", v);
      this.$emit("change", v);
      this.closeMenu();
      this.searchText = "";
    },
    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("change", null);
      this.closeMenu();
      this.searchText = "";
    },
    onCreateNew() {
      const text = this.searchText.trim();
      if (!text) return;
      this.$emit("update:modelValue", text);
      this.$emit("change", text);
      this.$emit("create", text);
      this.closeMenu();
      this.searchText = "";
    },
    onOutsideClick(e) {
      const root = this.$refs.root;
      const menu = this.$refs.menu;
      const target = e.target;
      if (root?.contains(target) || menu?.contains(target)) return;
      this.closeMenu();
    },
    onTriggerKeydown(e) {
      if (e.key === "Escape") {
        this.closeMenu();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleOpen();
      }
    },
  },
};
</script>

<style scoped>
.base-select {
  position: relative;
  width: 100%;
  --bs-border: #d2d6da;
  --bs-text: #344767;
  --bs-placeholder: #adb5bd;
  --bs-focus: #cb6bf3;
  --bs-focus-ring: rgba(203, 107, 243, 0.25);
  --bs-radius: 0.5rem;
  --bs-bg: #ffffff;
}
.base-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: var(--bs-bg);
  border: 1px solid var(--bs-border);
  border-radius: var(--bs-radius);
  padding: 0.5rem 0.85rem;
  font-size: 0.875rem;
  color: var(--bs-text);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
  line-height: 1.4;
}
.base-select--sm .base-select__trigger {
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
}
.base-select--lg .base-select__trigger {
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
}
.base-select__trigger:focus,
.base-select__trigger:focus-visible {
  outline: none;
  border-color: var(--bs-focus);
  box-shadow: 0 0 0 3px var(--bs-focus-ring);
}
.base-select--open .base-select__trigger {
  border-color: var(--bs-focus);
  box-shadow: 0 0 0 3px var(--bs-focus-ring);
}
.base-select--error .base-select__trigger {
  border-color: #ef5350;
}
.base-select--disabled .base-select__trigger {
  background: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
}
.base-select__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.base-select__placeholder {
  color: var(--bs-placeholder);
  font-weight: 400;
}
.base-select__chevron {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  color: var(--bs-text);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.base-select--open .base-select__chevron {
  transform: rotate(180deg);
}
.base-select__menu {
  position: absolute;
  top: calc(100% + 4px);
  bottom: auto;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: var(--bs-radius);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  z-index: 9999;
  max-height: 260px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.base-select__menu--drop-up {
  top: auto;
  bottom: calc(100% + 4px);
}
.base-select__search {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f3f5;
  flex-shrink: 0;
}
.base-select__list {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.base-select__option {
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
  color: var(--bs-text);
  cursor: pointer;
  transition: background 0.12s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.base-select__option:hover {
  background: #f8f9fa;
}
.base-select__option--selected {
  background: rgba(203, 107, 243, 0.1);
  color: #7c4dff;
  font-weight: 600;
}
.base-select__option--selected:hover {
  background: rgba(203, 107, 243, 0.15);
}
.base-select__option--clear {
  color: #67748e;
  font-size: 0.78rem;
  border-bottom: 1px solid #f1f3f5;
}
.base-select__option--create {
  color: #cb0c9f;
  font-weight: 600;
  border-bottom: 1px solid #f1f3f5;
}
.base-select__option--create:hover {
  background: rgba(203, 12, 159, 0.08);
}
.base-select__option--empty {
  color: #adb5bd;
  font-style: italic;
  cursor: default;
  text-align: center;
  padding: 1rem;
}
.base-select__option--empty:hover {
  background: transparent;
}
.base-select__option--disabled {
  color: #adb5bd;
  cursor: not-allowed;
  opacity: 0.72;
}
.base-select__option--disabled:hover {
  background: transparent;
}
</style>
